import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getLatestPartnerTopUps } from '../../../../services/LedgerService';
import { formatPhoneForDisplay } from '../../../../utils/phoneFormat';
import InfoPopover from '../../InfoPopover';
import TopUpReveal from '../../TopUpReveal';
import { clickableRowProps } from '../../../../utils/clickableRow';

// Render-prop wrapper that gives each top-up row its own reveal-open
// state so a whole-row click opens the same details card the eye badge
// opens. Applies to both the desktop table row and the mobile list row.
const TopUpRevealRow = ({ children }) => {
    const [isRevealOpen, setIsRevealOpen] = useState(false);
    return children({ isRevealOpen, setIsRevealOpen });
};

// Live status for the most recent partner top-ups. The list polls every
// four seconds while a fresh row is still pending or under review so a
// user watching after an STK push sees the state settle without a
// manual refresh. Consumers control the surrounding card and the count
// of rows to show; the compact rendering used by the solo-user wallet
// dashboard also lives here so a change to the badge or copy stays in
// one place.
const UNDER_REVIEW_COPY = 'Milipot is verifying this top-up. You do not need to do anything. Do not retry.';
const UNSETTLED_TOP_UP_STATUSES = ['pending', 'under_review'];
const UNSETTLED_ROW_POLL_CEILING_MS = 15 * 60 * 1000;

const hasFreshUnsettledRow = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;
    const cutoff = Date.now() - UNSETTLED_ROW_POLL_CEILING_MS;
    return rows.some((row) => {
        if (!UNSETTLED_TOP_UP_STATUSES.includes(row?.status)) return false;
        const createdAt = row?.created_at ? new Date(row.created_at).getTime() : null;
        return createdAt !== null && createdAt >= cutoff;
    });
};

const topUpStatusBadge = (topUp) => {
    switch (topUp?.status) {
        case 'successful':   return { cls: 'text-success',   label: 'Successful' };
        case 'under_review': return { cls: 'text-info',      label: 'Under Review' };
        case 'pending':      return { cls: 'text-warning',   label: 'Pending' };
        case 'failed':       return { cls: 'text-danger',    label: 'Failed' };
        default:             return { cls: 'text-secondary', label: 'Unknown' };
    }
};

const formatTopUpAmount = (amount) =>
    amount != null
        ? `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
        : null;

const formatTopUpDate = (dateString) =>
    new Date(dateString).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

const LatestPartnerTopUps = ({ partnerId, title = 'Latest Top-Ups', containerRef, tourAttr, viewMoreLink = '/ledger?tab=top-ups' }) => {
    const queryClient = useQueryClient();
    const { data: latestTopUps, isFetching: latestTopUpsLoading } = useQuery({
        queryKey: ['latestPartnerTopUps', partnerId],
        queryFn: () => getLatestPartnerTopUps(partnerId).then(res => res.data.data),
        enabled: !!partnerId,
        staleTime: 0,
        refetchInterval: (query) => (hasFreshUnsettledRow(query.state.data) ? 4000 : false),
    });

    const refreshTopUps = () => {
        queryClient.invalidateQueries({ queryKey: ['latestPartnerTopUps', partnerId] });
    };

    return (
        <div className="col-12" ref={containerRef} {...(tourAttr ? { 'data-tour': tourAttr } : {})}>
            <div className="card shadow-sm border-0 border-start border-4 border-primary">
                <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center position-relative">
                    <h2 className="heading h5 mb-0 fw-bold text-secondary">{title}</h2>
                    <div className="d-flex align-items-center gap-2">
                        {latestTopUpsLoading && (
                            <span className="spinner-border spinner-border-sm text-primary" role="status" />
                        )}
                        <button
                            type="button"
                            className="btn btn-link p-0 text-primary d-flex align-items-center justify-content-center"
                            onClick={refreshTopUps}
                            title="Refresh top-ups"
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: '#F4F3FE',
                                border: '1px solid #E6E4FA',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(180deg)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(0deg)'}
                        >
                            <i className="bi bi-arrow-clockwise fs-5"></i>
                        </button>
                    </div>
                </div>
                <div className="card-body p-0">
                    {latestTopUps?.length > 0 ? (
                        <>
                            {/* Desktop and tablet: table layout to match Latest Transactions */}
                            <div className="table-responsive d-none d-md-block px-3 pt-2">
                                <table className="table verticle-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">Receipt</th>
                                            <th scope="col">Amount</th>
                                            <th scope="col">From</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className="text-end">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {latestTopUps.map((item) => {
                                            const { cls, label } = topUpStatusBadge(item);
                                            return (
                                                <TopUpRevealRow key={item.id}>
                                                    {({ isRevealOpen, setIsRevealOpen }) => (
                                                        <tr {...clickableRowProps(() => setIsRevealOpen(true))}>
                                                            <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                {item.post_mpesa_receipt_number ? (
                                                                    <span className="d-inline-flex align-items-center gap-2">
                                                                        <span>{item.post_mpesa_receipt_number}</span>
                                                                        <TopUpReveal topUp={item} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                                                    </span>
                                                                ) : (
                                                                    <TopUpReveal topUp={item} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                                                )}
                                                            </td>
                                                            <td className="fw-medium text-dark">
                                                                {item.amount != null
                                                                    ? formatTopUpAmount(item.amount)
                                                                    : <span className="text-muted fst-italic">Pending</span>}
                                                            </td>
                                                            <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                {item.source_phone_number
                                                                    ? formatPhoneForDisplay(item.source_phone_number)
                                                                    : '-'}
                                                            </td>
                                                            <td className={`fw-medium ${cls}`}>
                                                                {label}
                                                                {item.status === 'under_review' && (
                                                                    <InfoPopover
                                                                        id={`latest-partner-top-ups-under-review-${item.id}`}
                                                                        body={UNDER_REVIEW_COPY}
                                                                        ariaLabel="What does Under Review mean?"
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="text-end text-muted small">
                                                                {formatTopUpDate(item.post_mpesa_transaction_date ?? item.created_at)}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </TopUpRevealRow>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile: icon-led list rows, same shape as Latest Transactions */}
                            <ul className="list-group list-group-flush d-block d-md-none">
                                {latestTopUps.map((item) => {
                                    const { cls, label } = topUpStatusBadge(item);
                                    const hasReference = item.source_phone_number || item.post_mpesa_receipt_number;
                                    return (
                                        <TopUpRevealRow key={item.id}>
                                            {({ isRevealOpen, setIsRevealOpen }) => (
                                                <li
                                                    className="list-group-item py-3"
                                                    {...clickableRowProps(() => setIsRevealOpen(true))}
                                                >
                                                    <div className="d-flex gap-3">
                                                        <div
                                                            className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${cls}`}
                                                            style={{ width: 40, height: 40, background: 'rgba(108, 117, 125, 0.08)' }}
                                                        >
                                                            <i className="bi bi-arrow-down-circle" style={{ fontSize: '1.25rem' }} />
                                                        </div>
                                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                                                <span className="fw-semibold text-body d-inline-flex align-items-center gap-2">
                                                                    <span>
                                                                        {item.amount != null
                                                                            ? formatTopUpAmount(item.amount)
                                                                            : <span className="text-muted fst-italic">Pending</span>}
                                                                    </span>
                                                                    <TopUpReveal topUp={item} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                                                </span>
                                                                <span className={`fw-medium ${cls} text-nowrap small`}>
                                                                    {label}
                                                                    {item.status === 'under_review' && (
                                                                        <InfoPopover
                                                                            id={`latest-partner-top-ups-mobile-under-review-${item.id}`}
                                                                            body={UNDER_REVIEW_COPY}
                                                                            ariaLabel="What does Under Review mean?"
                                                                        />
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <div className="small text-muted mb-1">
                                                                {formatTopUpDate(item.post_mpesa_transaction_date ?? item.created_at)}
                                                            </div>
                                                            {hasReference ? (
                                                                <>
                                                                    {item.source_phone_number && (
                                                                        <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                            From {formatPhoneForDisplay(item.source_phone_number)}
                                                                        </div>
                                                                    )}
                                                                    {item.post_mpesa_receipt_number && (
                                                                        <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                            Receipt {item.post_mpesa_receipt_number}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="small text-muted fst-italic">
                                                                    Awaiting M-Pesa confirmation
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </li>
                                            )}
                                        </TopUpRevealRow>
                                    );
                                })}
                            </ul>
                        </>
                    ) : (
                        <p className="text-center text-muted py-4 mb-0">No top-ups recorded yet.</p>
                    )}
                </div>
                <div className="text-end px-3 py-2 border-top">
                    <Link to={viewMoreLink} className="btn btn-link btn-sm p-0">
                        View more
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LatestPartnerTopUps;
